import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  decideApprovalContract,
  overrideApprovalContract,
  withdrawApprovalContract,
  type ApprovalVerdict,
  type GetApprovalResponse
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { approvalKeys, taskKeys } from '../query-keys';

type Action =
  | { kind: 'decide'; id: string; verdict: ApprovalVerdict; comment: string }
  | { kind: 'override'; id: string; reason: string }
  | { kind: 'withdraw'; id: string };

/**
 * Acts on a Freigabe: decide, override, withdraw. Each one closes or hands
 * out tasks on the server, so tasks refresh along with the approvals.
 */
export function useApprovalAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: Action): Promise<GetApprovalResponse> => {
      if (action.kind === 'decide') {
        const path = decideApprovalContract.path.replace(':id', action.id);
        const { data } = await apiClient.post<GetApprovalResponse>(path, {
          verdict: action.verdict,
          comment: action.comment
        });
        return data;
      }
      if (action.kind === 'override') {
        const path = overrideApprovalContract.path.replace(':id', action.id);
        const { data } = await apiClient.post<GetApprovalResponse>(path, { reason: action.reason });
        return data;
      }
      const path = withdrawApprovalContract.path.replace(':id', action.id);
      const { data } = await apiClient.post<GetApprovalResponse>(path, undefined);
      return data;
    },
    onSuccess: async (data, action) => {
      queryClient.setQueryData(approvalKeys.detail(action.id), data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: approvalKeys.all }),
        queryClient.invalidateQueries({ queryKey: taskKeys.all })
      ]);
    }
  });
}
